import { useCallback, useContext, useEffect, useState } from 'react'
import mixpanel from 'mixpanel-browser'
import log from 'loglevel'
import { useLocation } from 'react-router-dom'
import { isEuProd, isProduction } from '../Utils/environment'
import { clearSessionConsent, useConsent } from '../context/ConsentContext'
import { AnalyticsContext } from '../context/AnalyticsProvider'
import { getPageCategoryAndName } from './analytics.helpers'
import {
  APP_ANALYTICS_EVENTS,
  Callback,
  EventProperties,
  mixpanelDevToken,
  mixpanelEuToken,
  mixpanelProdToken,
  MIXPANEL_EU_API_HOST,
  MIXPANEL_US_API_HOST,
  Options,
  UseAnalytics,
  UserTraits,
} from './analytics.constants'

// We will use this global map to ensure certain track events are only fired once
// per session across all instances & remounts of the custom hook
const onlyOnceMap: Map<string, boolean | string> = new Map()
let isInitialized = false
let isLoaded = false

/**
 * Resolves the Mixpanel project token and ingestion host for the current
 * deployment. The EU deployment sends to its own EU-residency project via the
 * EU host so event data physically stays in the EU; everyone else uses the US
 * host. EU is checked first so it wins even if isProduction() is also true.
 */
export const resolveMixpanelConfig = (): {
  token: string
  apiHost: string
} => ({
  token: isEuProd()
    ? mixpanelEuToken
    : isProduction()
    ? mixpanelProdToken
    : mixpanelDevToken,
  apiHost: isEuProd() ? MIXPANEL_EU_API_HOST : MIXPANEL_US_API_HOST,
})

// TODO: Add handling to toggle ON/OFF for local development
export const useAnalytics = (): UseAnalytics => {
  const [isMixpanelLoaded, setIsMixpanelLoaded] = useState<boolean>(false)
  const location = useLocation()
  const { analyticsService } = useContext(AnalyticsContext)
  const { consentStatus } = useConsent()
  // Mirror the GA gate (see AnalyticsProvider): outside the EU we always load;
  // in the EU we only load once the user has explicitly accepted consent.
  const canLoad = !isEuProd() || consentStatus === 'accepted'

  useEffect(() => {
    // Initialize Mixpanel once. This effect can run twice in development due to
    // React.StrictMode, so guard with a synchronous flag. In the EU, hold off
    // initializing until the user has accepted consent (canLoad).
    if (isInitialized || !canLoad) {
      return
    }
    const { token, apiHost } = resolveMixpanelConfig()
    isInitialized = true
    try {
      mixpanel.init(token, {
        api_host: apiHost,
        persistence: 'localStorage',
        track_pageview: false,
        loaded: () => {
          isLoaded = true
          setIsMixpanelLoaded(true)
        },
      })
    } catch (err) {
      log.error('Mixpanel failed to load in App: ', err)
      isInitialized = false
    }
  })

  // Honor consent changes once Mixpanel has loaded: losing consent (EU) opts the
  // user out of tracking and clears their stored data; regaining it opts back in.
  useEffect(() => {
    if (!isLoaded) {
      return
    }
    if (canLoad) {
      if (mixpanel.has_opted_out_tracking()) {
        mixpanel.opt_in_tracking()
      }
    } else {
      mixpanel.opt_out_tracking()
    }
  }, [canLoad])

  const hasMixpanelLoaded = (): boolean => {
    return !!isLoaded
  }

  /**
   * This method tracks all routes/pages a user enters throughout their session
   *
   * @param location <Location> - route location the user visited
   * @param properties <dictionary> - additional event properties
   * @returns void
   */
  const pageView = useCallback(
    (location: Location, properties?: EventProperties) => {
      if (!hasMixpanelLoaded()) {
        return
      }
      const { category, name } = getPageCategoryAndName(location)
      if (!name) {
        return
      }
      mixpanel.track(`Viewed ${name}`, { category, name, ...properties })
    },
    [],
  )

  // Track page views for users during session
  useEffect(() => {
    // We will take advantage of the location.key to ensure only 1 unique page view is sent
    // even if the useEffect is triggered multiple times on the same page from rerenders
    if (location.key === onlyOnceMap.get('lastLocationKey')) {
      return
    }
    onlyOnceMap.set('lastLocationKey', location.key)
    pageView(location as unknown as Location)
  }, [location, pageView])

  /**
   * Identify helps us track users & their session once they login to Trustero
   *
   * @param userId <string> - unique primary ID for a given user (Mixpanel distinct_id)
   * @param traits <dictionary> - profile data to associate with this userId
   * @param options <dictionary> - unused, kept for signature compatibility
   * @param callback <func> - invoked after the profile update request
   * @returns <void>
   */
  const identify = (
    userId: string,
    traits: UserTraits,
    options?: Options,
    callback?: Callback,
  ) => {
    if (!hasMixpanelLoaded()) return
    mixpanel.identify(userId)
    const { name, email, ...rest } = traits ?? {}
    // Map name/email to Mixpanel's reserved $name/$email so the People profile
    // renders correctly
    mixpanel.people.set(
      {
        ...(name !== undefined && { $name: name }),
        ...(email !== undefined && { $email: email }),
        ...rest,
      },
      callback,
    )
  }

  /**
   * This helper ensures a user is only identifed once per user session
   * Its preferred to use identify within a `useEffect` when possible, but if the component rerenders often then
   * this is a great option
   *
   * @param userId <string> - unqiue primary ID for a given user
   * @param traits <dictionary> - specific user data we want to include/associate with this userId
   * @param options <dictionary> - unused, kept for signature compatibility
   * @param callback <func> - invoked after the profile update request
   * @returns <void>
   */
  const identifyOnce = (
    userId: string,
    traits: UserTraits,
    options?: Options,
    callback?: Callback,
  ) => {
    // Since users can switch accounts, we should only identify each team once through this flow
    const companyName = traits.company?.name
    if (
      !hasMixpanelLoaded() ||
      onlyOnceMap.get(`identify-${userId}-${companyName}`)
    ) {
      return
    }
    onlyOnceMap.set(`identify-${userId}-${companyName}`, true)
    identify(userId, traits, options, callback)
  }

  /**
   * Track is one of the primary methods we will use to track any custom event w/ various properties
   * For each new event we created, you need to extend the APP_ANALYTICS_EVENTS constant with a new key/value/properties
   *
   * @param event <string> - must contain a valid key:value in APP_ANALYTICS_EVENTS
   * @param properties <dictionary> - key:value pair of custom field names and values
   * @param options <dictionary> - unused, kept for signature compatibility
   * @param callback <func> - invoked after the track request
   * @returns <void>
   */
  const track = (
    event: string,
    properties?: EventProperties,
    options?: Options,
    callback?: Callback,
  ) => {
    // GA mirrors product events from this shared path whenever its provider is
    // initialized (gated by its own consent handling, independent of Mixpanel).
    analyticsService?.track(event, properties)
    hasMixpanelLoaded() && mixpanel.track(event, properties, options, callback)
  }

  /**
   * This helper ensures a track event only fires 1 time per user session
   * Its preferred to use track within a `useEffect` when possible, but if the component rerenders often then
   * this is a great option
   *
   * @param event <string> - must contain a valid key:value in APP_ANALYTICS_EVENTS
   * @param properties <dictionary> - key:value pair of custom field names and values
   * @param options <dictionary> - unused, kept for signature compatibility
   * @param callback <func> - invoked after the track request
   * @returns <void>
   */
  const trackOnce = (
    event: string,
    properties?: EventProperties,
    options?: Options,
    callback?: Callback,
  ) => {
    if (onlyOnceMap.get(event)) {
      return
    }
    if (!analyticsService && !hasMixpanelLoaded()) {
      return
    }
    onlyOnceMap.set(event, true)
    track(event, properties, options, callback)
  }

  /**
   * Calling reset clears the identified user (distinct_id) and their stored
   * super properties / profile for the current session.
   *
   * This is called upon user Logout once their session is over
   *
   * @returns <void>
   */
  const reset = () => {
    hasMixpanelLoaded() && mixpanel.reset()
    clearSessionConsent()
  }

  return {
    events: APP_ANALYTICS_EVENTS,
    identify,
    identifyOnce,
    pageView,
    reset,
    track,
    trackOnce,
    isMixpanelLoaded,
  }
}
